// Helper for the "deny" path of the scope-consent screen.
//
// When a user denies consent, we return them to the application's destination (the `redirect_uri`
// from the sign-in request) with `error=Consent denied` appended as a query parameter, preserving
// the original `state` so the app can correlate the response.

export const CONSENT_DENIED_ERROR = 'Consent denied';

// Only these URL schemes are safe to hand to window.location.assign(). Blocking everything else
// prevents a crafted redirect_uri with a javascript:/data: scheme from executing script (XSS).
const ALLOWED_REDIRECT_PROTOCOLS = ['https:', 'http:'];

/**
 * Builds the deny redirect URL from the request's search params. Returns undefined if there is no
 * `redirect_uri`, if it does not parse as a URL, or if it uses a non-http(s) scheme.
 *
 * Note: the OAuth `redirect_uri` is validated by Cognito against the app client's registered
 * callback URLs before the user reaches this screen, so destination origin is already vetted. The
 * scheme check here defends against the javascript:/data: XSS vector that URL parsing alone allows.
 */
export function buildConsentDeniedRedirectUrl(
  search: string
): string | undefined {
  const params = new URLSearchParams(search);
  const redirectUri = params.get('redirect_uri');
  if (!redirectUri) {
    return undefined;
  }

  let url: URL;
  try {
    url = new URL(redirectUri);
  } catch {
    return undefined;
  }

  if (!ALLOWED_REDIRECT_PROTOCOLS.includes(url.protocol)) {
    return undefined;
  }

  url.searchParams.set('error', CONSENT_DENIED_ERROR);
  // Preserve state so the destination app can correlate the denied response with its request.
  const state = params.get('state');
  if (state) {
    url.searchParams.set('state', state);
  }
  return url.toString();
}

/**
 * Redirects the browser to the destination with the consent-denied error. Returns false if there
 * was no valid redirect_uri to send the user to.
 */
export function redirectConsentDenied(search: string): boolean {
  const target = buildConsentDeniedRedirectUrl(search);
  if (!target) {
    return false;
  }
  window.location.assign(target);
  return true;
}
